import { useState } from 'react'
import { useProgressSheet, usePaymentRecords } from '@/hooks/useProgressSheet'

interface ProgressSheetPanelProps {
  relationshipId: string
  readOnly: boolean
}

// Digitizes the A-H overall-competency checklist, remarks, signatures, and payment
// ledger from the physical CARS Learning Form A in-car tracking sheet (migration 066).
export function ProgressSheetPanel({ relationshipId, readOnly }: ProgressSheetPanelProps) {
  const { items, remarks, setRemarks, studentSignedAt, instructorSignedAt, saving, toggleItem, saveRemarks, sign } = useProgressSheet(relationshipId)
  const { records, addRecord } = usePaymentRecords(relationshipId)
  const [newDate, setNewDate] = useState('')
  const [newAmount, setNewAmount] = useState('')
  const [newBalance, setNewBalance] = useState('')
  const [error, setError] = useState('')

  return (
    <div>
      <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 10 }}>Overall competency checklist</div>
      {items.map(item => (
        <div key={item.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0' }}>
          <div
            onClick={() => !readOnly && toggleItem(item.key, !item.checkedAt)}
            style={{
              width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, cursor: readOnly ? 'default' : 'pointer',
              border: `1.5px solid ${item.checkedAt ? '#0F7C6C' : 'rgba(27,42,74,0.25)'}`,
              background: item.checkedAt ? '#0F7C6C' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', font: '700 11px Inter,sans-serif',
            }}
          >
            {item.checkedAt ? '✓' : ''}
          </div>
          <div style={{ font: '400 13px/1.4 Inter, sans-serif', color: '#1B2A4A' }}>{item.label}</div>
        </div>
      ))}

      <div style={{ marginTop: 16 }}>
        <label className='field-label'>Instructor remarks</label>
        <textarea
          value={remarks}
          onChange={e => setRemarks(e.target.value)}
          onBlur={() => !readOnly && saveRemarks(remarks)}
          readOnly={readOnly}
          rows={3}
          className='field-input'
          style={{ width: '100%', resize: 'vertical', fontFamily: 'Inter, sans-serif' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 14 }}>
        <div>
          <div style={{ font: '500 12px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 6 }}>Student signature</div>
          {studentSignedAt ? (
            <div style={{ font: '400 12.5px Inter, sans-serif', color: '#0F7C6C' }}>Signed {new Date(studentSignedAt).toLocaleDateString()}</div>
          ) : (
            <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Not yet signed</div>
          )}
        </div>
        <div>
          <div style={{ font: '500 12px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 6 }}>Instructor signature</div>
          {instructorSignedAt ? (
            <div style={{ font: '400 12.5px Inter, sans-serif', color: '#0F7C6C' }}>Signed {new Date(instructorSignedAt).toLocaleDateString()}</div>
          ) : !readOnly ? (
            <button onClick={() => sign('instructor')} disabled={saving} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(15,124,108,0.3)', background: 'transparent', font: '500 12px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer' }}>
              Sign
            </button>
          ) : (
            <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Not yet signed</div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 22, borderTop: '1px solid rgba(27,42,74,0.08)', paddingTop: 16 }}>
        <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 10 }}>Payment ledger</div>
        {records.length === 0 ? (
          <div style={{ color: 'rgba(27,42,74,0.4)', marginBottom: 10 }}>No payments logged yet.</div>
        ) : records.map(r => (
          <div key={r.id} style={{ display: 'flex', gap: 14, padding: '6px 0', borderBottom: '1px solid rgba(27,42,74,0.05)', font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
            <div style={{ flex: 1 }}>{new Date(r.paymentDate).toLocaleDateString()}</div>
            <div>${r.amount.toFixed(2)}</div>
            <div style={{ color: 'rgba(27,42,74,0.55)' }}>{r.balance != null ? `Balance: $${r.balance.toFixed(2)}` : ''}</div>
          </div>
        ))}
        {!readOnly && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <input type='date' value={newDate} onChange={e => setNewDate(e.target.value)} className='field-input' style={{ flex: '1 1 140px' }} />
            <input type='number' placeholder='Amount' value={newAmount} onChange={e => setNewAmount(e.target.value)} className='field-input' style={{ flex: '1 1 100px' }} />
            <input type='number' placeholder='Balance (optional)' value={newBalance} onChange={e => setNewBalance(e.target.value)} className='field-input' style={{ flex: '1 1 140px' }} />
            <button className='btn-teal' style={{ width: 'auto', padding: '9px 16px' }}
              onClick={async () => {
                const amount = Number(newAmount)
                if (!newDate || !amount || amount <= 0) { setError('Enter a date and a positive amount.'); return }
                const r = await addRecord(newDate, amount, newBalance ? Number(newBalance) : null)
                if (r.ok) { setNewDate(''); setNewAmount(''); setNewBalance(''); setError('') } else setError(r.error ?? 'Could not save payment.')
              }}>
              + Log payment
            </button>
          </div>
        )}
        {error && <div className='banner-error' style={{ marginTop: 10 }}>{error}</div>}
      </div>
    </div>
  )
}
